import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface Policy {
  readonly policyId: string;
  readonly policyCode: string;
  readonly policyName: string;
  readonly classification: string;
}

const CLASSIFICATIONS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'];

/** M26 — Data Governance Dashboard (§20). Stewardship & classification policies;
 *  policy authoring (POST /v1/dwh/governance/policies) surfaced over the guarded
 *  GOVERNANCE_MANAGE endpoint. */
export function M26DataGovernanceDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<Policy[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [policyName, setPolicyName] = useState('');
  const [classification, setClassification] = useState('INTERNAL');

  const load = useCallback((): void => {
    setLoading(true);
    apiRequest<{ data: Policy[]; total: number }>('/v1/dwh/governance/policies', { token })
      .then((r) => { setRows(r.data); setTotal(r.total); setError(null); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  function onCreate(e: FormEvent): void {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    setError(null);
    apiRequest('/v1/dwh/governance/policies', {
      method: 'POST',
      token,
      body: { policyName, classification },
    })
      .then(() => { setNotice(t('m26.governance.policyCreated')); setPolicyName(''); load(); })
      .catch((err) => setError((err as Error).message))
      .finally(() => setBusy(false));
  }

  return (
    <main aria-label="data-governance-dashboard">
      <h1>{t('m26.governance.title')}</h1>
      <form aria-label="policy-create-form" onSubmit={onCreate}>
        <label htmlFor="policy-name">{t('m26.governance.policyNameLabel')}</label>
        <input id="policy-name" value={policyName} onChange={(e) => setPolicyName(e.target.value)} required />
        <label htmlFor="policy-classification">{t('m26.governance.classificationLabel')}</label>
        <select id="policy-classification" value={classification} onChange={(e) => setClassification(e.target.value)}>
          {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button type="submit" disabled={busy}>{t('m26.governance.createPolicy')}</button>
      </form>
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m26.governance.loading')}</p> : (
        <>
          <p aria-label="policy-count">{t('m26.governance.count', { count: total })}</p>
          <table aria-label="policy-table">
            <thead><tr><th>{t('m26.governance.colCode')}</th><th>{t('m26.governance.colName')}</th><th>{t('m26.governance.colClassification')}</th></tr></thead>
            <tbody>{rows.map((p) => <tr key={p.policyId}><td>{p.policyCode}</td><td>{p.policyName}</td><td>{p.classification}</td></tr>)}</tbody>
          </table>
        </>
      )}
    </main>
  );
}
